import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

export class VanityBuilderComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    // Listen to changes in top/handle selections globally across the product page
    document.addEventListener('change', this.#onSelectionChange, { signal });
    document.addEventListener('click', this.#onButtonClick, { signal });
    document.addEventListener('click', this.#onCardClick, { signal });

    // Listen to main product variant selection changes across the page
    const target = this.closest('.shopify-section, dialog, product-information') || document;
    target.addEventListener(StandardEvents.productSelect, this.#onMainProductSelect, { signal });

    // Watch main product form variant ID changes
    const mainForm = this.#getMainForm();
    if (mainForm) {
      const variantInput = mainForm.querySelector('input[name="id"]');
      if (variantInput) {
        const observer = new MutationObserver(() => this.updateTotals());
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    // Initial total update
    requestAnimationFrame(() => this.updateTotals());
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #getMainForm() {
    return document.querySelector('product-form-component form, form[action*="/cart/add"]');
  }

  #getMainVariantId() {
    const mainForm = this.#getMainForm();
    const variantInput = mainForm?.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }
    // Fallback to dataset or page context
    return this.dataset.mainVariantId || null;
  }

  #onSelectionChange = (event) => {
    const target = event.target;
    if (target.matches('input[name="vanity-top"], input[name="vanity-handle"], input[name="quantity"], input[name="id"]')) {
      this.updateTotals();
    }
  };

  #onMainProductSelect = (event) => {
    event.promise?.then(({ detail }) => {
      if (detail?.variant) {
        this.dataset.mainVariantPrice = detail.variant.price;
        this.dataset.mainVariantId = detail.variant.id;
        this.updateTotals();
      }
    }).catch(() => {});
  };

  #onButtonClick = (event) => {
    const btn = event.target.closest('[data-vanity-add-to-cart]');
    if (btn) {
      event.preventDefault();
      this.addToCart(btn);
    }
  };

  #onCardClick = (event) => {
    const label = event.target.closest('label.vanity-top-card, label.vanity-handle-card');
    if (!label) return;

    const input = label.querySelector('input[type="radio"]');
    if (!input || input.disabled) return;

    const pickerBlock = label.closest('.vanity-picker-block');
    const isOptional = pickerBlock?.dataset.optional === 'true';

    if (!isOptional) return;

    if (label.classList.contains('is-selected')) {
      event.preventDefault();
      input.checked = false;
      label.classList.remove('is-selected');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  updateTotals() {
    // 0. Update is-selected class on all cards based on current input checked state
    document.querySelectorAll('label.vanity-top-card, label.vanity-handle-card').forEach((lbl) => {
      const inp = lbl.querySelector('input[type="radio"]');
      if (inp && inp.checked) {
        lbl.classList.add('is-selected');
      } else {
        lbl.classList.remove('is-selected');
      }
    });

    // 1. Get main vanity price
    let mainPrice = 0;
    const mainForm = this.#getMainForm();
    
    if (this.dataset.mainVariantPrice) {
      mainPrice = parseInt(this.dataset.mainVariantPrice, 10);
    } else {
      const mainPriceAttr = this.getAttribute('data-main-price');
      if (mainPriceAttr) {
        mainPrice = parseInt(mainPriceAttr, 10);
      }
    }

    // 2. Get top selection from document
    const selectedTop = document.querySelector('input[name="vanity-top"]:checked');
    let topPrice = 0;
    let topTitle = '';
    if (selectedTop) {
      topPrice = parseInt(selectedTop.dataset.price || '0', 10);
      topTitle = selectedTop.dataset.title || '';
    }

    // 3. Get handle selection from document
    const selectedHandle = document.querySelector('input[name="vanity-handle"]:checked');
    let handlePrice = 0;
    let handleTitle = '';
    if (selectedHandle) {
      handlePrice = parseInt(selectedHandle.dataset.price || '0', 10);
      handleTitle = selectedHandle.dataset.title || '';
    }

    // 4. Update Header Subtitles
    // Nothing selected reads as nothing chosen. The "— Optional" suffix was noise on a
    // step that is already titled "(optional)", so the subtitle now only ever names the
    // product the shopper picked.
    const topSubtitleEl = document.querySelector('[data-vanity-top-subtitle]');
    if (topSubtitleEl) {
      topSubtitleEl.textContent = selectedTop ? `— ${topTitle}` : '';
    }

    const handleSubtitleEl = document.querySelector('[data-vanity-handle-subtitle]');
    if (handleSubtitleEl) {
      handleSubtitleEl.textContent = selectedHandle ? `— ${handleTitle}` : '';
    }

    // 5. Calculate total
    const quantityInput = document.querySelector('vanity-builder input[name="quantity"]') || mainForm?.querySelector('input[name="quantity"]');
    const qty = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    const buildTotal = (mainPrice + topPrice + handlePrice) * qty;

    // 6. Format currency
    const moneyFormat = window.Shopify?.money_format || '${{amount}}';
    const currency = window.Shopify?.currency?.active || 'AUD';
    const formattedTotal = formatMoney(buildTotal, moneyFormat, currency);

    // 7. Update display
    const totalEls = document.querySelectorAll('[data-vanity-build-total-amount]');
    totalEls.forEach((el) => {
      el.textContent = formattedTotal;
    });

    // 8. Auto sequence step badges & counters
    this.updateStepNumbers();
  }

  updateStepNumbers() {
    const pickers = Array.from(document.querySelectorAll('.vanity-picker-block'));
    if (!pickers.length) return;

    const visiblePickers = pickers.filter((p) => {
      return p.offsetWidth > 0 || p.offsetHeight > 0 || getComputedStyle(p).display !== 'none';
    });
    const totalSteps = visiblePickers.length;

    visiblePickers.forEach((picker, index) => {
      const stepNum = index + 1; // Pickers start at Step 1
      const badge = picker.querySelector('.vanity-step-badge');
      const counter = picker.querySelector('.vanity-step-counter');

      if (badge) badge.textContent = stepNum;
      if (counter) counter.textContent = `(${stepNum}/${totalSteps})`;
    });
  }

  async addToCart(button) {
    if (button.hasAttribute('data-loading')) return;
    button.setAttribute('data-loading', 'true');

    const mainVariantId = this.#getMainVariantId();
    const selectedTop = document.querySelector('input[name="vanity-top"]:checked');
    const selectedHandle = document.querySelector('input[name="vanity-handle"]:checked');
    
    const quantityInput = document.querySelector('vanity-builder input[name="quantity"]') || this.#getMainForm()?.querySelector('input[name="quantity"]');
    const qty = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    const items = [];

    // Main Vanity Product
    if (mainVariantId) {
      items.push({
        id: parseInt(mainVariantId, 10),
        quantity: qty
      });
    }

    // Top Product (if selected and has valid variant ID)
    if (selectedTop && selectedTop.dataset.variantId && selectedTop.dataset.variantId !== 'none') {
      items.push({
        id: parseInt(selectedTop.dataset.variantId, 10),
        quantity: qty
      });
    }

    // Handle Product (if selected and has valid variant ID)
    if (selectedHandle && selectedHandle.dataset.variantId) {
      items.push({
        id: parseInt(selectedHandle.dataset.variantId, 10),
        quantity: qty
      });
    }

    if (items.length === 0) {
      button.removeAttribute('data-loading');
      return;
    }

    try {
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: item.id.toString(),
            quantity: item.quantity,
          })),
          promise: deferredEventPromise.promise,
        })
      );

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const responseData = await response.json();

      if (responseData.status) {
        console.error('Add to cart failed:', responseData.message);
        button.removeAttribute('data-loading');
        alert(responseData.message || 'Failed to add items to cart.');
        return;
      }

      // Fetch cart and notify system
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.json`);
      const cartData = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
        detail: {
          items: cartData.items,
          source: 'vanity-builder',
          didError: false,
        },
      });

      // Update cart items component if active
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.fetchCartData) {
        await cartItemsComponent.fetchCartData();
      }

      // Show temporary added state on button
      button.removeAttribute('data-loading');
      button.setAttribute('data-added', 'true');
      setTimeout(() => button.removeAttribute('data-added'), 2000);

      // Open theme drawer cart if available
      const cartDrawer = document.querySelector('cart-drawer-component, theme-drawer#cart-drawer');
      if (cartDrawer && cartDrawer.open) {
        cartDrawer.open();
      } else {
        const cartIcon = document.querySelector('.header-actions__cart-icon, [href="/cart"]');
        cartIcon?.click();
      }
    } catch (err) {
      console.error('Error adding vanity build to cart:', err);
      button.removeAttribute('data-loading');
    }
  }
}

if (!customElements.get('vanity-builder')) {
  customElements.define('vanity-builder', VanityBuilderComponent);
}
